import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getAllCategories } from '../../utils/services/categoriesAPI';
import { getDocumentsByCommunication } from '../../utils/services/documentAPI';
import { getAllFlowElements, getAllBlockElements } from '../../utils/services/elementAPI';
import Loader from '../../components/Loader';
import { DocumentCard } from './DocumentCard';
import styled from 'styled-components';
import { Icon, message } from 'antd';
import { SlidersHorizontalIcon, ChevronDownIconSVG } from '../../images/svgIcons';
import { NEW_TEMPLATE_SECTION } from './translationValues';
import ScrollContainerV2 from '../../components/ScrollContainerV2';

const ScrollContainerAdapter = ({ className, ...props }) => (
  <ScrollContainerV2 customClassName={className} {...props} />
);

const GridContainer = styled(ScrollContainerAdapter)`
  width: 100%;
  padding: 32px 80px;
  flex: 1;
  /* overflow-y: auto; handled by ScrollContainerV2 */
  background: linear-gradient(to bottom right, #f9fafb, #f3f4f6) !important;
  transition: padding 0.3s ease;
  
  /* Override ScrollContainerV2 scrollbar styles to match original UI */
  &&::-webkit-scrollbar {
    width: 8px !important;
  }
  &&::-webkit-scrollbar-track {
    background: transparent !important;
    box-shadow: none !important;
  }
  &&::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background: #d1d5db !important;
    box-shadow: none !important;
  }
  &&::-webkit-scrollbar-thumb:hover {
     background: #d1d5db !important;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const HeaderTop = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin-bottom: 8px;
  font-size: 30px;
  background: linear-gradient(to right, #111827, #4b5563);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #6b7280;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const DropdownWrapper = styled.div`
  position: relative;
`;

const SortButton = styled.button`
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  padding: 8px 14px;
  background: white;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    border-color: #a78bfa;
  }
  svg:last-child {
    transition: transform 0.2s;
    transform: ${props => (props.open ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  width: 192px;
  padding: 4px;
  background: white;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: ${props => (props.visible ? 'block' : 'none')};
`;

const MenuItem = styled.button`
  border: none;
  border-radius: 8px;
  width: 100%;
  padding: 10px 16px;
  background: white;
  font-size: 14px;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #f9fafb;
  }
  svg {
    color: #6366f1;
  }
`;

const EmptyState = styled.div`
  padding: 80px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #6b7280;
`;

const EmptyIcon = styled.div`
  margin-bottom: 16px;
  border-radius: 50%;
  height: 96px;
  width: 96px;
  background: #e5e7eb;
  display: flex;
  justify-content: center;
  align-items: center;
  svg {
    color: #9ca3af;
  }
`;

const EmptyTitle = styled.p`
  margin-bottom: 4px;
  font-size: 18px;
`;

const EmptySubtitle = styled.p`
  font-size: 14px;
`;

const DocumentsGrid = styled.div`
  width: 100%;
  display: grid;
  justify-content: start;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 32px;
  @media (min-width: 1520px) {
    grid-template-columns: repeat(auto-fit, minmax(225px, 1fr));
  }
  .observer_div {
    width: 100%;
    text-align: center;
    grid-column: 1 / -1;
  }
`;

const DocumentGrid = ({
  selectedView,
  organizationView,
  searchQuery,
  qaStatus,
  selectedTags,
  selectedFolder,
  sortBy,
  setSortBy,
  clearAllFilters,
  allLanguages,
}) => {
  const { t } = useTranslation(['NEW_TEMPLATE_SECTION']);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);

  const [loading, setLoading] = useState(false);

  const [categoryList, setCategoryList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dataCount, setDataCount] = useState(0);

  const [page, setPage] = useState(1);
  const [refreshPage, setRefreshPage] = useState(0);

  const fetchingRef = useRef(false);
  const hasFetchedAllRef = useRef(false);

  const fetchCategoriesList = async () => {
    try {
      const response = await getAllCategories(
        false,
        selectedView === 'documents' ? 'template' : 'element',
      );
      if (response?.statusCode === 507 && response?.data) {
        const categories = response.data.map(item => ({
          categoryId: item.id,
          categoryName: item.name,
        }));
        setCategoryList(categories);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const initalMountRef = useRef(true);
  useEffect(() => {
    if (!initalMountRef.current) {
      clearAllFilters();
      clearData();
    }
    fetchCategoriesList();
    initalMountRef.current = false;
  }, [selectedView]);

  const fetchTemplates = async (pageNum, isInitialFetch) => {
    if (isInitialFetch) {
      setLoading(true);
    }

    const orgViewKeyMap = {
      categories: 'categoryId',
      folders: 'folderId',
      teams: 'resourceGroupId',
      languages: 'language',
    };

    let statusMap;
    if (selectedView === 'documents') {
      statusMap = { waiting: true, rejected: false };
    } else {
      statusMap = { waiting: 'qacomplete', rejected: 'rejected' };
    }

    let payload = {
      page: pageNum,
      limit: 12,
      rgPermission: 'write',

      ...(searchQuery && { name: searchQuery }),
      ...(selectedTags.length && { tags: selectedTags }),
      ...(sortBy === 'name' && { asc: true }),

      ...(selectedFolder !== 'all' &&
        orgViewKeyMap[organizationView] && {
        [orgViewKeyMap[organizationView]]: selectedFolder.split('+')[0],
      }),
    };

    const isAllFetched = response => {
      return isInitialFetch
        ? response.data?.length === response?.dataCount
        : [...templates, ...response.data].length === response?.dataCount;
    };

    if (selectedView === 'documents') {
      payload = {
        ...payload,
        ...(statusMap[qaStatus] !== undefined && {
          waitingForApproval: statusMap[qaStatus],
        }),
      };
      const response = await getDocumentsByCommunication(qaStatus !== null, payload);
      if ([3215, 3216].includes(response?.statusCode) && response?.data) {
        setTemplates(prev => (isInitialFetch ? response.data : [...prev, ...response.data]));
        setDataCount(response?.dataCount);
        if (isAllFetched(response)) {
          hasFetchedAllRef.current = true;
        }
        fetchingRef.current = false;
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FETCH_DOCUMENTS_ERROR.key,
            NEW_TEMPLATE_SECTION.FETCH_DOCUMENTS_ERROR.value,
          ),
        );
      }
    } else if (selectedView === 'reusable_pages') {
      payload = {
        ...payload,
        ...(statusMap[qaStatus] && {
          qaReadyStatus: statusMap[qaStatus],
        }),
      };

      const response = await getAllFlowElements(
        pageNum,
        true,
        null,
        searchQuery,
        selectedTags.length ? selectedTags : '',
        'write',
        qaStatus !== null,
        true,
        payload,
      );
      if (response?.statusCode === 2605 && response?.data) {
        setTemplates(prev => (isInitialFetch ? response.data : [...prev, ...response.data]));
        setDataCount(response?.dataCount);
        if (isAllFetched(response)) {
          hasFetchedAllRef.current = true;
        }
        fetchingRef.current = false;
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FETCH_PAGES_ERROR.key,
            NEW_TEMPLATE_SECTION.FETCH_PAGES_ERROR.value,
          ),
        );
      }
    } else {
      payload = {
        ...payload,
        ...(statusMap[qaStatus] && {
          qaReadyStatus: statusMap[qaStatus],
        }),
      };
      const response = await getAllBlockElements(
        pageNum,
        true,
        null,
        searchQuery,
        selectedTags.length ? selectedTags : '',
        'write',
        qaStatus !== null,
        true,
        payload,
      );
      if (response?.statusCode === 2505 && response?.data) {
        setTemplates(prev => (isInitialFetch ? response.data : [...prev, ...response.data]));
        setDataCount(response?.dataCount);
        if (isAllFetched(response)) {
          hasFetchedAllRef.current = true;
        }
        fetchingRef.current = false;
      } else {
        message.error(
          t(
            NEW_TEMPLATE_SECTION.FETCH_BLOCKS_ERROR.key,
            NEW_TEMPLATE_SECTION.FETCH_BLOCKS_ERROR.value,
          ),
        );
      }
    }
    setLoading(false);
  };

  const clearData = () => {
    setTemplates([]);
    setDataCount(0);
    setPage(1);
    fetchingRef.current = false;
    hasFetchedAllRef.current = false;
  };

  useEffect(() => {
    clearData();
    fetchTemplates(1, true);
  }, [searchQuery, qaStatus, selectedTags, selectedFolder, sortBy, refreshPage]);

  useEffect(() => {
    if (page > 1) {
      fetchTemplates(page, false);
    }
  }, [page]);

  const handleFetchMore = useCallback(() => {
    if (!fetchingRef.current && !hasFetchedAllRef.current) {
      setPage(prev => prev + 1);
      fetchingRef.current = true;
    }
  }, []);

  const getFolderName = folderId => {
    const folderNames = {
      all:
        selectedView === 'documents'
          ? t(NEW_TEMPLATE_SECTION.ALL_DOCUMENTS.key, NEW_TEMPLATE_SECTION.ALL_DOCUMENTS.value)
          : selectedView === 'reusable_pages'
            ? t(
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_PAGES.key,
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_PAGES.value,
            )
            : t(
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_SECTIONS.key,
              NEW_TEMPLATE_SECTION.ALL_REUSABLE_SECTIONS.value,
            ),
    };
    return folderNames[folderId] || folderId.substring(folderId.indexOf('+') + 1);
  };

  const displayText =
    selectedView === 'documents'
      ? `${dataCount} ${t(
        NEW_TEMPLATE_SECTION.COMMUNICATIONS.key,
        NEW_TEMPLATE_SECTION.COMMUNICATIONS.value,
      )}`
      : selectedView === 'reusable_pages'
        ? `${dataCount} ${t(
          NEW_TEMPLATE_SECTION.REUSABLE_PAGES.key,
          NEW_TEMPLATE_SECTION.REUSABLE_PAGES.value,
        )}`
        : `${dataCount} ${t(
          NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.key,
          NEW_TEMPLATE_SECTION.REUSABLE_SECTIONS.value,
        )}`;

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <GridContainer fetchMoreData={handleFetchMore}>
          <Header>
            <HeaderTop>
              <div>
                <Title>{getFolderName(selectedFolder)}</Title>
                <Subtitle>{displayText}</Subtitle>
              </div>

              <Actions>
                <DropdownWrapper>
                  <SortButton open={sortMenuOpen} onClick={() => setSortMenuOpen(!sortMenuOpen)}>
                    <SlidersHorizontalIcon />
                    {t(NEW_TEMPLATE_SECTION.SORT.key, NEW_TEMPLATE_SECTION.SORT.value)}
                    <ChevronDownIconSVG />
                  </SortButton>

                  <DropdownMenu visible={sortMenuOpen}>
                    <MenuItem
                      onClick={() => {
                        setSortBy('date');
                        setSortMenuOpen(false);
                      }}
                    >
                      <span>
                        {t(
                          NEW_TEMPLATE_SECTION.DATE_MODIFIED.key,
                          NEW_TEMPLATE_SECTION.DATE_MODIFIED.value,
                        )}
                      </span>
                      {sortBy === 'date' && <Icon type="check" size={16} />}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setSortBy('name');
                        setSortMenuOpen(false);
                      }}
                    >
                      <span>
                        {t(NEW_TEMPLATE_SECTION.NAME_AZ.key, NEW_TEMPLATE_SECTION.NAME_AZ.value)}
                      </span>
                      {sortBy === 'name' && <Icon type="check" size={16} />}
                    </MenuItem>
                  </DropdownMenu>
                </DropdownWrapper>
              </Actions>
            </HeaderTop>
          </Header>

          {templates.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Icon type="filter" size={40} />
              </EmptyIcon>
              <EmptyTitle>
                {selectedView === 'documents'
                  ? t(
                    NEW_TEMPLATE_SECTION.NO_DOCUMENTS_FOUND.key,
                    NEW_TEMPLATE_SECTION.NO_DOCUMENTS_FOUND.value,
                  )
                  : selectedView === 'reusable_pages'
                    ? t(
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_PAGES_FOUND.key,
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_PAGES_FOUND.value,
                    )
                    : t(
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_SECTIONS_FOUND.key,
                      NEW_TEMPLATE_SECTION.NO_REUSABLE_SECTIONS_FOUND.value,
                    )}
              </EmptyTitle>
              <EmptySubtitle>
                {t(
                  NEW_TEMPLATE_SECTION.TRY_ADJUSTING_FILTERS.key,
                  NEW_TEMPLATE_SECTION.TRY_ADJUSTING_FILTERS.value,
                )}
              </EmptySubtitle>
            </EmptyState>
          ) : (
            <DocumentsGrid>
              {templates.map(template => (
                <DocumentCard
                  key={template.id}
                  selectedView={selectedView}
                  categoryList={categoryList}
                  template={template}
                  highlightLanguage={
                    organizationView === 'languages' ? selectedFolder.split('+')[0] : null
                  }
                  setRefreshPage={setRefreshPage}
                  allLanguages={allLanguages}
                />
              ))}
              {!loading && templates.length > 0 && (
                <div className="observer_div">
                  {templates.length < dataCount ? (
                    <>
                      <Icon type="loading" />{' '}
                      {t(NEW_TEMPLATE_SECTION.LOADING.key, NEW_TEMPLATE_SECTION.LOADING.value)}
                    </>
                  ) : (
                    <p>
                      {t(
                        NEW_TEMPLATE_SECTION.NO_MORE_DATA.key,
                        NEW_TEMPLATE_SECTION.NO_MORE_DATA.value,
                      )}
                    </p>
                  )}
                </div>
              )}
            </DocumentsGrid>
          )}
        </GridContainer>
      )}
    </>
  );
};

export default DocumentGrid;
